import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Users, Copy, Share2, RefreshCw, Eye, Clock, ShieldCheck } from "lucide-react";

const FAMILY_BASE = "https://nikah-match.com/family-view";

export default function FamilyMode() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/family/status"); setStatus(data); } catch { setStatus({ enabled: false }); } };
  useEffect(() => { load(); }, []);

  const link = status?.token ? `${FAMILY_BASE}/${status.token}` : "";

  const toggle = async (on) => {
    setBusy(true);
    try { const { data } = await api.post(on ? "/family/enable" : "/family/disable", {}); setStatus(data.enabled !== undefined ? data : { ...status, enabled: on }); if (on) await load(); toast.success(on ? "Family Mode enabled" : "Family Mode turned off"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!window.confirm("Generate a new link? The old link will stop working immediately.")) return;
    setBusy(true);
    try { const { data } = await api.post("/family/regenerate", {}); setStatus(data); toast.success("New link generated — the old one is now revoked"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(link); toast.success("Link copied"); } catch { toast.error("Could not copy"); } };
  const share = async () => { try { if (navigator.share) await navigator.share({ title: "Nikah Match — Family View", text: "Please review my suggested matches privately:", url: link }); else await copy(); } catch {} };

  return (
    <div className="mx-auto max-w-2xl" data-testid="family-mode-page">
      <div className="text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-mint"><Users className="h-7 w-7 text-emerald-800" /></div>
        <h1 className="font-serif text-3xl font-bold text-emerald-950 sm:text-4xl">Involve a Wali or family member</h1>
        <p className="mx-auto mt-3 max-w-lg text-slate-600">Share a private, read-only link so a trusted guardian can review your suggested matches — without ever accessing your account, messages, or contact details.</p>
      </div>

      {status === null ? <Skeleton className="mt-8 h-28 rounded-2xl" /> : (
        <>
          <div className="mt-8 flex items-center justify-between rounded-2xl border border-emerald-900/10 bg-white p-5" data-testid="family-toggle-card">
            <div>
              <p className="font-semibold text-emerald-900">Family Mode</p>
              <p className="text-sm text-slate-500">{status.enabled ? "Your family link is active." : "Turn on to create a private link."}</p>
            </div>
            <Switch checked={!!status.enabled} disabled={busy} onCheckedChange={toggle} data-testid="family-mode-switch" />
          </div>

          {status.enabled && status.token && (
            <>
              <div className="mt-5 rounded-3xl border border-emerald-900/10 bg-white p-6 text-center" data-testid="family-link-card">
                <div className="mx-auto inline-block rounded-2xl bg-white p-3 ring-1 ring-emerald-900/10">
                  <QRCodeCanvas value={link} size={200} fgColor="#0B4A3F" level="M" data-testid="family-qr" />
                </div>
                <p className="mt-4 break-all text-sm text-slate-500" data-testid="family-link-text">{link}</p>
              </div>

              <div className="mt-5 space-y-3">
                <Button onClick={share} data-testid="family-share-btn" className="w-full rounded-full bg-emerald-800 py-6 text-base text-white hover:bg-emerald-900"><Share2 className="mr-2 h-5 w-5" /> Share with Wali / family</Button>
                <Button onClick={copy} variant="outline" data-testid="family-copy-btn" className="w-full rounded-full border-emerald-800/30 py-6 text-base text-emerald-900 hover:bg-mint"><Copy className="mr-2 h-5 w-5" /> Copy Link</Button>
                <a href={`/family-view/${status.token}`} target="_blank" rel="noopener noreferrer" data-testid="family-preview-btn" className="flex items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold text-emerald-700 hover:text-emerald-900">
                  <Eye className="h-4 w-4" /> Preview what your Wali will see
                </a>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-4">
                <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 text-center" data-testid="family-views">
                  <Eye className="mx-auto mb-1 h-5 w-5 text-emerald-700" />
                  <p className="text-2xl font-bold text-emerald-900">{status.viewCount ?? 0}</p>
                  <p className="text-xs text-slate-500">Times viewed</p>
                </div>
                <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 text-center" data-testid="family-lastviewed">
                  <Clock className="mx-auto mb-1 h-5 w-5 text-emerald-700" />
                  <p className="text-sm font-bold text-emerald-900">{status.lastViewedAt ? new Date(status.lastViewedAt).toLocaleDateString() : "—"}</p>
                  <p className="text-xs text-slate-500">Last viewed</p>
                </div>
              </div>

              <button onClick={regenerate} disabled={busy} data-testid="family-regenerate-btn" className="mx-auto mt-6 flex items-center gap-2 text-sm font-semibold text-amber-600 hover:text-amber-700">
                <RefreshCw className="h-4 w-4" /> Generate a new link (revokes the old one)
              </button>
            </>
          )}

          <div className="mt-8 flex items-start gap-3 rounded-2xl bg-mint/40 p-4 text-sm text-emerald-900/80" data-testid="family-privacy-note">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
            <p>The link is read-only. Your guardian can see your curated matches but can never log in, message anyone, or view contact details. You can revoke access anytime.</p>
          </div>
        </>
      )}
    </div>
  );
}
